// ===================================================================
// components/WordleGame.tsx
// "Mot du jour" — Wordle africain. Un mot de vocabulaire différent
// chaque jour, par langue, avec grille de couleurs et clavier.
// ===================================================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useTheme } from './ThemeContext';
import FloatingBackButton from './FloatingBackButton';
import TribalBorder from './TribalBorder';
import { getWordlePool } from '../utils/vocabExtract';
import { SESSION_DATE, recordActivity } from '../hooks/useDailyStreak';
import { LanguageCode } from '../types/types';
import { Illustration, Picto, EmojiText } from './common/Picto';
import './WordleGame.css';

interface WordleGameProps {
  languageCode: LanguageCode;
  onBack: () => void;
}

type LetterStatus = 'correct' | 'present' | 'absent';
type GameStatus = 'playing' | 'won' | 'lost';

const MAX_GUESSES = 6;
const EPOCH = new Date('2025-01-01T00:00:00');

const LANG_META: Partial<Record<LanguageCode, { flag: string; name: string }>> = {
  wf: { flag: '🇸🇳', name: 'Wolof' },
  ba: { flag: '🇲🇱', name: 'Bambara' },
  la: { flag: '🇨🇩', name: 'Lingala' },
  ff: { flag: '🇬🇳', name: 'Peul' },
  co: { flag: '🏛️', name: 'Copte' },
  sw: { flag: '🇰🇪', name: 'Swahili' },
  yo: { flag: '🇳🇬', name: 'Yoruba' },
};

const KEYBOARD_ROWS = [
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
  ['ENTER', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', 'BACK'],
];

const toDateString = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const isYesterday = (dateStr: string, today: string): boolean => {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + 1);
  return toDateString(d) === today;
};

function dailyIndex(dateStr: string, langCode: string, poolLen: number): number {
  if (poolLen <= 0) return 0;
  let h = 0;
  const seed = `${dateStr}|${langCode}`;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return h % poolLen;
}

function puzzleNumber(dateStr: string): number {
  const d = new Date(dateStr + 'T00:00:00');
  return Math.max(1, Math.floor((d.getTime() - EPOCH.getTime()) / 86_400_000) + 1);
}

function evaluateGuess(guess: string[], answer: string[]): LetterStatus[] {
  const result: LetterStatus[] = new Array(guess.length).fill('absent');
  const remaining: (string | null)[] = [...answer];

  for (let i = 0; i < guess.length; i++) {
    if (guess[i] === answer[i]) { result[i] = 'correct'; remaining[i] = null; }
  }
  for (let i = 0; i < guess.length; i++) {
    if (result[i] === 'correct') continue;
    const idx = remaining.indexOf(guess[i]);
    if (idx !== -1) { result[i] = 'present'; remaining[idx] = null; }
  }
  return result;
}

function extraLetters(words: string[]): string[] {
  const set = new Set<string>();
  words.forEach(w => {
    Array.from(w.toLocaleUpperCase()).forEach(ch => {
      if (!/[A-Z]/.test(ch)) set.add(ch);
    });
  });
  return Array.from(set).sort();
}

interface WordleStats {
  played: number;
  wins: number;
  currentStreak: number;
  maxStreak: number;
  lastResultDate: string | null;
  distribution: number[];
}

const emptyStats = (): WordleStats => ({
  played: 0, wins: 0, currentStreak: 0, maxStreak: 0, lastResultDate: null,
  distribution: new Array(MAX_GUESSES).fill(0),
});

interface SavedPuzzleState {
  date: string;
  answer: string;
  clue: string;
  illustration?: string;
  guesses: string[];
  status: GameStatus;
}

const WordleGame: React.FC<WordleGameProps> = ({ languageCode, onBack }) => {
  const { theme } = useTheme();
  const { t } = useTranslation();

  const meta = useMemo(
    () => LANG_META[languageCode] ?? { flag: '🌍', name: languageCode.toUpperCase() },
    [languageCode]
  );
  const statsKey = `${languageCode}-wordle-stats`;
  const puzzleKey = `${languageCode}-wordle-${SESSION_DATE}`;

  const pool = useMemo(() => getWordlePool(languageCode), [languageCode]);

  const puzzle = useMemo(() => {
    if (pool.length === 0) return null;
    const idx = dailyIndex(SESSION_DATE, languageCode, pool.length);
    return pool[idx];
  }, [pool, languageCode]);

  const answerLetters = useMemo(
    () => (puzzle ? Array.from(puzzle.word.toLocaleUpperCase()) : []),
    [puzzle]
  );

  const [guesses, setGuesses] = useState<string[]>([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [status, setStatus] = useState<GameStatus>('playing');
  const [shake, setShake] = useState(false);
  const [stats, setStats] = useState<WordleStats>(emptyStats);
  const [copied, setCopied] = useState(false);
  const [lastXpGained, setLastXpGained] = useState<number | null>(null);

  // ── Chargement de la progression du jour + des stats ──────────────
  useEffect(() => {
    if (!puzzle) return;
    try {
      const raw = localStorage.getItem(puzzleKey);
      if (raw) {
        const saved: SavedPuzzleState = JSON.parse(raw);
        if (saved.answer === puzzle.word.toLocaleUpperCase()) {
          setGuesses(saved.guesses);
          setStatus(saved.status);
        }
      }
    } catch { /* ignore */ }
    try {
      const rawStats = localStorage.getItem(statsKey);
      if (rawStats) setStats({ ...emptyStats(), ...JSON.parse(rawStats) });
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puzzleKey, statsKey]);

  const persistPuzzle = useCallback((nextGuesses: string[], nextStatus: GameStatus) => {
    if (!puzzle) return;
    try {
      const toSave: SavedPuzzleState = {
        date: SESSION_DATE, answer: puzzle.word.toLocaleUpperCase(), clue: puzzle.clue,
        illustration: puzzle.illustration, guesses: nextGuesses, status: nextStatus,
      };
      localStorage.setItem(puzzleKey, JSON.stringify(toSave));
    } catch { /* ignore */ }
  }, [puzzle, puzzleKey]);

  const finishGame = useCallback((won: boolean, attempts: number) => {
    setStats(prev => {
      const currentStreak = won
        ? (prev.lastResultDate && isYesterday(prev.lastResultDate, SESSION_DATE) ? prev.currentStreak + 1 : 1)
        : 0;
      const distribution = [...prev.distribution];
      if (won) distribution[attempts - 1] = (distribution[attempts - 1] ?? 0) + 1;
      const next: WordleStats = {
        played: prev.played + 1,
        wins: prev.wins + (won ? 1 : 0),
        currentStreak,
        maxStreak: Math.max(prev.maxStreak, currentStreak),
        lastResultDate: SESSION_DATE,
        distribution,
      };
      try { localStorage.setItem(statsKey, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });

    const xpGained = won ? 20 + (MAX_GUESSES - attempts) * 8 : 5;
    try {
      const xpKey = `${languageCode}-totalXP`;
      const newTotal = (parseInt(localStorage.getItem(xpKey) || '0', 10)) + xpGained;
      localStorage.setItem(xpKey, String(newTotal));
      window.dispatchEvent(new CustomEvent('xpUpdated', { detail: { newTotal, gained: xpGained, source: 'wordle' } }));
    } catch { /* ignore */ }

    recordActivity();
    setLastXpGained(xpGained);
  }, [statsKey, languageCode]);

  const submitGuess = useCallback(() => {
    if (status !== 'playing' || !puzzle) return;
    const letters = Array.from(currentGuess);
    if (letters.length !== answerLetters.length) {
      setShake(true);
      setTimeout(() => setShake(false), 420);
      return;
    }
    const nextGuesses = [...guesses, currentGuess];
    const won = currentGuess === puzzle.word.toLocaleUpperCase();
    const lost = !won && nextGuesses.length >= MAX_GUESSES;
    const nextStatus: GameStatus = won ? 'won' : lost ? 'lost' : 'playing';

    setGuesses(nextGuesses);
    setCurrentGuess('');
    setStatus(nextStatus);
    persistPuzzle(nextGuesses, nextStatus);

    if (nextStatus !== 'playing') finishGame(won, nextGuesses.length);
  }, [status, puzzle, currentGuess, answerLetters.length, guesses, persistPuzzle, finishGame]);

  const handleKey = useCallback((key: string) => {
    if (status !== 'playing') return;
    if (key === 'ENTER') { submitGuess(); return; }
    if (key === 'BACK') { setCurrentGuess(g => g.slice(0, -1)); return; }
    setCurrentGuess(g => (g.length < answerLetters.length ? g + key : g));
  }, [status, answerLetters.length, submitGuess]);

  // ── Clavier physique ───────────────────────────────────────────────
  useEffect(() => {
    if (status !== 'playing') return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'Enter') { handleKey('ENTER'); return; }
      if (e.key === 'Backspace') { handleKey('BACK'); return; }
      if (e.key.length === 1 && /^\p{L}$/u.test(e.key)) { handleKey(e.key.toLocaleUpperCase()); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [status, handleKey]);

  // ── Statut des touches du clavier virtuel ──────────────────────────
  const keyStatuses = useMemo(() => {
    const map: Record<string, LetterStatus> = {};
    const rank: Record<LetterStatus, number> = { absent: 0, present: 1, correct: 2 };
    guesses.forEach(g => {
      const letters = Array.from(g);
      const evalResult = evaluateGuess(letters, answerLetters);
      letters.forEach((l, i) => {
        const prev = map[l];
        if (!prev || rank[evalResult[i]] > rank[prev]) map[l] = evalResult[i];
      });
    });
    return map;
  }, [guesses, answerLetters]);

  const extraRow = useMemo(() => extraLetters(pool.map(e => e.word)), [pool]);

  // ── Partage façon Wordle ───────────────────────────────────────────
  const shareText = useMemo(() => {
    if (!puzzle) return '';
    const grid = guesses.map(g => {
      const letters = Array.from(g);
      return evaluateGuess(letters, answerLetters)
        .map(s => (s === 'correct' ? '🟩' : s === 'present' ? '🟨' : '⬛'))
        .join('');
    }).join('\n');
    const scoreLine = status === 'won' ? `${guesses.length}/${MAX_GUESSES}` : `X/${MAX_GUESSES}`;
    return `Jogolinga · ${meta.name} ${meta.flag} #${puzzleNumber(SESSION_DATE)}\n${scoreLine}\n\n${grid}\n\njogolinga.com`;
  }, [puzzle, guesses, answerLetters, status, meta]);

  const handleShare = useCallback(async () => {
    if (navigator.share) {
      try { await navigator.share({ text: shareText }); return; } catch { /* annulé */ }
    }
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  }, [shareText]);

  const card = 'var(--mg-card)';
  const border = 'var(--mg-border)';
  const textCol = 'var(--mg-text)';
  const sub = 'var(--mg-text-muted)';
  const gold = 'var(--mg-gold)';

  if (!puzzle) {
    return (
      <div className="wordle-screen" data-theme={theme}>
        <FloatingBackButton onBack={onBack} position="top-left" variant="minimal" />
        <div className="wordle-unavailable">
          <div className="wordle-unavailable-icon"><Picto name="lettres" /></div>
          <h2>{t('wordle.comingSoon', 'Bientôt disponible')}</h2>
          <p>{t('wordle.comingSoonText', "Le mot du jour pour cette langue arrive prochainement.")}</p>
        </div>
      </div>
    );
  }

  const tileStatusFor = (rowGuess: string, colIndex: number): LetterStatus | null => {
    const letters = Array.from(rowGuess);
    if (letters.length !== answerLetters.length) return null;
    return evaluateGuess(letters, answerLetters)[colIndex];
  };

  return (
    <div className="wordle-screen" data-theme={theme}>
      <TribalBorder />
      <FloatingBackButton onBack={onBack} position="top-left" variant="minimal" />

      <div className="wordle-header">
        <h1 className="wordle-title"><EmojiText text={meta.flag} /> {t('wordle.title', 'Mot du jour')}</h1>
        <p className="wordle-subtitle">{meta.name} · #{puzzleNumber(SESSION_DATE)}</p>
      </div>

      <div className="wordle-clue-card" style={{ background: card, borderColor: border, color: textCol }}>
        <span className="wordle-clue-label" style={{ color: sub }}>{t('wordle.clueLabel', 'Indice — ce mot signifie :')}</span>
        <span className="wordle-clue-text">{puzzle.clue}</span>
      </div>

      <div className={`wordle-grid${shake ? ' wordle-grid--shake' : ''}`}>
        {Array.from({ length: MAX_GUESSES }).map((_, rowIdx) => {
          const submittedGuess = guesses[rowIdx];
          const isCurrentRow = rowIdx === guesses.length && status === 'playing';
          const rowLetters = submittedGuess
            ? Array.from(submittedGuess)
            : isCurrentRow
              ? Array.from(currentGuess)
              : [];
          return (
            <div key={rowIdx} className="wordle-row" style={{ gridTemplateColumns: `repeat(${answerLetters.length}, 1fr)` }}>
              {answerLetters.map((_, colIdx) => {
                const letter = rowLetters[colIdx] ?? '';
                const tileStatus = submittedGuess ? tileStatusFor(submittedGuess, colIdx) : null;
                return (
                  <div
                    key={colIdx}
                    className={`wordle-tile${tileStatus ? ` wordle-tile--${tileStatus}` : ''}${letter && !tileStatus ? ' wordle-tile--filled' : ''}`}
                  >
                    {letter}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {status === 'playing' && (
        <div className="wordle-keyboard">
          {extraRow.length > 0 && (
            <div className="wordle-kb-row wordle-kb-row--extra">
              {extraRow.map(k => (
                <button key={k} className={`wordle-key wordle-key--extra${keyStatuses[k] ? ` wordle-key--${keyStatuses[k]}` : ''}`} onClick={() => handleKey(k)}>{k}</button>
              ))}
            </div>
          )}
          {KEYBOARD_ROWS.map((row, i) => (
            <div key={i} className="wordle-kb-row">
              {row.map(k => {
                if (k === 'ENTER') return <button key={k} className="wordle-key wordle-key--wide" onClick={() => handleKey(k)}>{t('wordle.enter', 'Entrer')}</button>;
                if (k === 'BACK') return <button key={k} className="wordle-key wordle-key--wide" onClick={() => handleKey(k)}>⌫</button>;
                return (
                  <button key={k} className={`wordle-key${keyStatuses[k] ? ` wordle-key--${keyStatuses[k]}` : ''}`} onClick={() => handleKey(k)}>{k}</button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {status !== 'playing' && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="wordle-results"
          style={{ background: card, borderColor: border }}
        >
          <div className="wordle-results-emoji"><EmojiText text={status === 'won' ? '🏆' : '📖'} /></div>
          <h2 style={{ color: textCol }}>
            {status === 'won' ? t('wordle.won', 'Bravo !') : t('wordle.lost', 'Dommage !')}
          </h2>
          <p style={{ color: sub }}>
            {status === 'won'
              ? t('wordle.wonText', { count: guesses.length, defaultValue: 'Trouvé en {{count}} essai(s) !' })
              : t('wordle.lostText', 'Le mot était :')}
            {status === 'lost' && <strong style={{ color: gold }}> {puzzle.word}</strong>}
          </p>
          {puzzle.illustration && <div className="wordle-results-illustration"><Illustration value={puzzle.illustration} /></div>}

          <div className="wordle-stats-row">
            <div className="wordle-stat"><span className="wordle-stat-value" style={{ color: gold }}>{stats.played}</span><span className="wordle-stat-label" style={{ color: sub }}>{t('wordle.played', 'Joués')}</span></div>
            <div className="wordle-stat"><span className="wordle-stat-value" style={{ color: gold }}>{stats.played > 0 ? Math.round((stats.wins / stats.played) * 100) : 0}%</span><span className="wordle-stat-label" style={{ color: sub }}>{t('wordle.winRate', 'Réussite')}</span></div>
            <div className="wordle-stat"><span className="wordle-stat-value" style={{ color: gold }}><Picto name="feu" /> {stats.currentStreak}</span><span className="wordle-stat-label" style={{ color: sub }}>{t('wordle.streak', 'Série')}</span></div>
            <div className="wordle-stat"><span className="wordle-stat-value" style={{ color: gold }}>{stats.maxStreak}</span><span className="wordle-stat-label" style={{ color: sub }}>{t('wordle.maxStreak', 'Record')}</span></div>
          </div>

          {lastXpGained !== null && (
            <p className="wordle-xp-earned" style={{ color: gold }}>
              {t('wordle.xpEarned', { xp: lastXpGained, defaultValue: '+{{xp}} XP' })}
            </p>
          )}

          <button className="wordle-share-btn" onClick={handleShare}>
            {copied ? t('wordle.copied', 'Copié !') : <EmojiText text={t('wordle.share', '📋 Partager mon résultat')} />}
          </button>
          <p className="wordle-next-hint" style={{ color: sub }}>{t('wordle.comeBack', 'Reviens demain pour un nouveau mot !')}</p>

          <FloatingBackButton onBack={onBack} mode="inline" variant="ghost" text={t('wordle.backToMenu', 'Retour aux mini-jeux') as string} />
        </motion.div>
      )}
    </div>
  );
};

export default WordleGame;
